/**
 * GEDCOM Date Parser
 *
 * Parses GEDCOM date formats into structured date objects.
 * Supports exact dates, year-only dates, and date qualifiers.
 *
 * Part of Phase 4 - Slice 4: Date Parsing
 */

/**
 * Date qualifier types from GEDCOM specification
 */
export type DateQualifier = 'ABT' | 'BEF' | 'AFT' | 'CAL' | 'EST';

/**
 * Parsed date structure
 */
export interface ParsedDate {
  /** ISO 8601 format date string (YYYY-MM-DD), only for exact dates */
  date: string | null;

  /** Extracted year (available for all date types) */
  year: number | null;

  /** Date qualifier (about, before, after, calculated, estimated) */
  qualifier: DateQualifier | null;
}

/**
 * GEDCOM month abbreviations to numeric values
 */
const GEDCOM_MONTHS: Record<string, number> = {
  JAN: 1,
  FEB: 2,
  MAR: 3,
  APR: 4,
  MAY: 5,
  JUN: 6,
  JUL: 7,
  AUG: 8,
  SEP: 9,
  OCT: 10,
  NOV: 11,
  DEC: 12,
};

/**
 * Valid GEDCOM date qualifiers
 */
const VALID_QUALIFIERS = new Set<string>(['ABT', 'BEF', 'AFT', 'CAL', 'EST']);

/**
 * Parse a GEDCOM date string into a structured ParsedDate object
 *
 * Supports:
 * - Exact dates: "24 MAY 1819" → {date: "1819-05-24", year: 1819, qualifier: null}
 * - Year-only: "1820" → {date: null, year: 1820, qualifier: null}
 * - Qualifiers: "ABT 1850" → {date: null, year: 1850, qualifier: "ABT"}
 * - Partial dates: "MAY 1850" → {date: null, year: 1850, qualifier: null}
 * - Qualified exact dates: "ABT 24 MAY 1850" → {date: "1850-05-24", year: 1850, qualifier: "ABT"}
 *
 * @param dateString - Raw GEDCOM date value (e.g., "24 MAY 1819", "ABT 1850")
 * @returns ParsedDate object or null if parsing fails
 *
 * @example
 * parseGedcomDate("24 MAY 1819") // {date: "1819-05-24", year: 1819, qualifier: null}
 * parseGedcomDate("1820") // {date: null, year: 1820, qualifier: null}
 * parseGedcomDate("ABT 1850") // {date: null, year: 1850, qualifier: "ABT"}
 * parseGedcomDate("MAY 1850") // {date: null, year: 1850, qualifier: null}
 * parseGedcomDate(null) // null
 */
export function parseGedcomDate(dateString: string | null | undefined): ParsedDate | null {
  // Handle null/undefined/empty input
  if (!dateString || typeof dateString !== 'string' || !dateString.trim()) {
    return null;
  }

  // Normalize: trim and convert to uppercase
  const normalized = dateString.trim().toUpperCase();

  // Split into tokens
  const tokens = normalized.split(/\s+/);

  if (tokens.length === 0) {
    return null;
  }

  // Check for qualifier prefix
  let qualifier: DateQualifier | null = null;
  let remainingTokens = tokens;

  if (VALID_QUALIFIERS.has(tokens[0] as DateQualifier)) {
    qualifier = tokens[0] as DateQualifier;
    remainingTokens = tokens.slice(1);
  }

  if (remainingTokens.length === 0) {
    // Only qualifier, no date (invalid)
    return null;
  }

  // Parse the date portion
  return parseDateTokens(remainingTokens, qualifier);
}

/**
 * Parse date tokens (after qualifier extraction)
 *
 * Supports:
 * - [YYYY] - Year only
 * - [MMM, YYYY] - Month and year
 * - [DD, MMM, YYYY] - Full date
 *
 * @param tokens - Date tokens (without qualifier)
 * @param qualifier - Extracted qualifier or null
 * @returns ParsedDate object or null if parsing fails
 */
function parseDateTokens(tokens: string[], qualifier: DateQualifier | null): ParsedDate | null {
  if (tokens.length === 1) {
    // Case 1: Year only (e.g., "1820")
    const year = parseYear(tokens[0]);
    if (year === null) {
      return null;
    }

    return {
      date: null,
      year,
      qualifier,
    };
  }

  if (tokens.length === 2) {
    // Case 2: Month + Year (e.g., "MAY 1850")
    const month = parseMonth(tokens[0]);
    const year = parseYear(tokens[1]);

    if (month === null || year === null) {
      return null;
    }

    // We have month and year, but no day - cannot create full ISO date
    return {
      date: null,
      year,
      qualifier,
    };
  }

  if (tokens.length === 3) {
    // Case 3: Day + Month + Year (e.g., "24 MAY 1819")
    const day = parseDay(tokens[0]);
    const month = parseMonth(tokens[1]);
    const year = parseYear(tokens[2]);

    if (day === null || month === null || year === null) {
      return null;
    }

    // Validate date components
    if (!isValidDate(year, month, day)) {
      console.warn(
        `[GEDCOM Date Parser] Invalid date components: year=${year}, month=${month}, day=${day}`
      );
      return null;
    }

    // Create ISO 8601 date string
    const isoDate = formatISODate(year, month, day);

    return {
      date: isoDate,
      year,
      qualifier,
    };
  }

  // Unknown format (too many tokens)
  console.warn(
    `[GEDCOM Date Parser] Unknown date format with ${tokens.length} tokens: ${tokens.join(' ')}`
  );
  return null;
}

/**
 * Parse a year token to a number
 *
 * @param token - Year token (e.g., "1819")
 * @returns Year number or null if invalid
 */
function parseYear(token: string): number | null {
  const year = parseInt(token, 10);

  if (isNaN(year)) {
    return null;
  }

  // Validate reasonable year range (1-9999)
  // GEDCOM supports years from 1 to 9999
  if (year < 1 || year > 9999) {
    console.warn(`[GEDCOM Date Parser] Year out of range: ${year}`);
    return null;
  }

  return year;
}

/**
 * Parse a month token to a month number (1-12)
 *
 * @param token - Month token (e.g., "MAY")
 * @returns Month number (1-12) or null if invalid
 */
function parseMonth(token: string): number | null {
  const month = GEDCOM_MONTHS[token];

  if (month === undefined) {
    console.warn(`[GEDCOM Date Parser] Unknown month abbreviation: ${token}`);
    return null;
  }

  return month;
}

/**
 * Parse a day token to a day number (1-31)
 *
 * @param token - Day token (e.g., "24")
 * @returns Day number or null if invalid
 */
function parseDay(token: string): number | null {
  const day = parseInt(token, 10);

  if (isNaN(day)) {
    return null;
  }

  // Basic range check (detailed validation in isValidDate)
  if (day < 1 || day > 31) {
    return null;
  }

  return day;
}

/**
 * Validate that year, month, and day form a valid calendar date
 *
 * Checks for:
 * - Month in range 1-12
 * - Day appropriate for the month
 * - Leap year handling for February
 *
 * @param year - Year number
 * @param month - Month number (1-12)
 * @param day - Day number
 * @returns true if date is valid, false otherwise
 */
function isValidDate(year: number, month: number, day: number): boolean {
  // Month range check
  if (month < 1 || month > 12) {
    return false;
  }

  // Days in each month (non-leap year)
  const daysInMonth = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

  // Adjust for leap year
  if (isLeapYear(year)) {
    daysInMonth[1] = 29;
  }

  // Check day is within valid range for month
  const maxDay = daysInMonth[month - 1];
  return day >= 1 && day <= maxDay;
}

/**
 * Check if a year is a leap year
 *
 * Rules:
 * - Divisible by 4: leap year
 * - Divisible by 100: not a leap year
 * - Divisible by 400: leap year
 *
 * @param year - Year number
 * @returns true if leap year, false otherwise
 */
function isLeapYear(year: number): boolean {
  if (year % 400 === 0) {
    return true;
  }
  if (year % 100 === 0) {
    return false;
  }
  if (year % 4 === 0) {
    return true;
  }
  return false;
}

/**
 * Format year, month, and day as ISO 8601 date string (YYYY-MM-DD)
 *
 * @param year - Year number
 * @param month - Month number (1-12)
 * @param day - Day number
 * @returns ISO 8601 date string
 */
function formatISODate(year: number, month: number, day: number): string {
  const yearStr = year.toString().padStart(4, '0');
  const monthStr = month.toString().padStart(2, '0');
  const dayStr = day.toString().padStart(2, '0');

  return `${yearStr}-${monthStr}-${dayStr}`;
}
